import { toJS } from '@rehaje/reactive'
import { isArray, isNotEmpty } from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import {
  SchemaMaxItemsError,
  SchemaMinItemsError,
  SchemaTransformError
} from '../../errors'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type {
  RuntimeSchemaSpecific,
  SchemaArraySpecific,
  TypedSchema,
  TypedSpecific
} from '../../types'

export class SchemaArrayDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any[] | TypedSpecific | undefined,
    schema: SchemaArraySpecific,
    initialize: boolean
  ) {
    if (value === undefined && (initialize || schema.required))
      value = schema.default
    if (value === undefined || isArray(value)) {
      value?.forEach((item, index) => {
        const defaults = super.defaultsMerge(
          item,
          this.getIn(schema, [index]),
          initialize
        )
        if (defaults === undefined) return
        value[index] = defaults
      })
    }
    return value
  }

  override getIn(schema: SchemaArraySpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    const items = schema.items
    if (items) {
      const index = path[0]
      let result: TypedSchema
      if (isNaN(index))
        result = isArray(items)
          ? super.getIn(items[0], path.slice(1))
          : super.getIn(items, path.slice(1))
      else {
        result = isArray(items)
          ? super.getIn(items[index as number], path.slice(1))
          : super.getIn(items, path.slice(1))
      }
      if (!result && schema.additionalItems) {
        result = super.getIn(schema.additionalItems, path.slice(1))
      }
      return result ?? UnknownSchema
    }
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaArraySpecific,
    path: PropertyKey[] = []
  ): SchemaArraySpecific {
    if (typeof schema.infer === 'function') {
      return schema.infer.call(
        this,
        target,
        (arg0 = target, arg1 = schema, arg2 = path) => {
          return this.infer(arg0, { ...arg1, infer: undefined }, arg2)
        }
      )
    }

    if (isArray(schema.items) && (target === undefined || isArray(target))) {
      const items: TypedSchema[] = []
      for (
        let i = 0;
        i < Math.max(target?.length ?? 0, schema.items.length);
        i++
      ) {
        const item = schema.items[i] ?? schema.additionalItems
        if (item)
          items.push(super.infer(target?.[i], item, path.concat(i), target))
      }
      return {
        ...schema,
        items,
        get value() {
          return toJS(target)
        }
      }
    } else if (schema.items) {
      return {
        ...schema,
        items: super.infer(
          target?.[0],
          schema.items as TypedSchema,
          path.concat(0),
          target
        ),
        get value() {
          return toJS(target)
        }
      }
    }

    return {
      ...schema,
      type: 'Schema.Array'
    }
  }

  inferItemTypeScript(item: TypedSchema, path: PropertyKey[]) {
    if (item.type === 'Schema.Void' && item.types?.length) {
      const types: string[] = []
      for (const type of item.types) {
        const inferred = super.inferTypeScript(type, path)
        if (types.includes(inferred)) continue
        types.push(inferred)
      }
      return types.join('|')
    }
    return super.inferTypeScript(item, path)
  }

  override inferOnlySchema(
    schema: SchemaArraySpecific,
    path: PropertyKey[] = []
  ): SchemaArraySpecific {
    return {
      additionalItems: schema.additionalItems
        ? super.inferOnlySchema(schema.additionalItems, path.concat('*'))
        : undefined,
      items: isArray(schema.items)
        ? schema.items.map((item, index) =>
            super.inferOnlySchema(item, path.concat(index))
          )
        : schema.items
          ? super.inferOnlySchema(schema.items, path.concat(0))
          : undefined,
      type: 'Schema.Array'
    }
  }

  override inferTypeScript(
    schema: SchemaArraySpecific,
    path: PropertyKey[]
  ): string {
    const items = schema.items

    if (isArray(items)) {
      return `[${items
        .map((item, index) => {
          return this.inferItemTypeScript(item, path.concat(index))
        })
        .join(',')}${
        schema.additionalItems
          ? `${items.length > 0 ? ',' : ''}...${this.inferItemTypeScript(schema.additionalItems, path.concat(0))}`
          : ''
      }]`
    } else if (items) {
      return `Array<${this.inferItemTypeScript(items, path.concat(0)) || 'unknown'}>`
    } else if (schema.additionalItems) {
      return `Array<${this.inferItemTypeScript(schema.additionalItems, path.concat(0)) || 'unknown'}>`
    } else return `Array<unknown>`
  }

  override prune(
    target: any,
    schema: SchemaArraySpecific,
    path?: PropertyKey[]
  ): any {
    if (!isArray(target)) return target

    const array = target.slice()

    if (isArray(schema.items)) {
      for (let i = 0; i < schema.items.length; i++) {
        array[i] = super.prune(
          array[i],
          schema.items[i],
          path?.concat(i),
          array
        )
      }
      if (schema.additionalItems) {
        for (let i = schema.items.length; i < array.length; i++) {
          array[i] = super.prune(
            array[i],
            schema.additionalItems,
            path?.concat(i),
            array
          )
        }
      }
    } else if (schema.items) {
      for (let i = 0; i < array.length; i++) {
        array[i] = super.prune(array[i], schema.items, path?.concat(i), array)
      }
    } else if (schema.additionalItems) {
      for (let i = 0; i < array.length; i++) {
        array[i] = super.prune(
          array[i],
          schema.additionalItems,
          path?.concat(i),
          array
        )
      }
    }

    return array
  }

  override transform(
    target: any,
    schema: SchemaArraySpecific,
    path: PropertyKey[] = []
  ) {
    if (!isArray(target)) {
      if (target === null && schema.nullable) return target
      if (target !== schema.fallback)
        this.raise(new SchemaTransformError(schema.name))
      return schema.fallback === undefined ? [] : schema.fallback
    }
    const array = target.slice()
    if (isArray(schema.items)) {
      let i = 0
      for (; i < schema.items.length; i++) {
        array[i] = super.transform(
          array[i],
          schema.items[i],
          path.concat(i),
          array
        )
      }
      for (let j = i; j < array.length; j++) {
        const additionalItems = schema.additionalItems
        if (additionalItems) {
          array[j] = super.transform(
            array[j],
            additionalItems,
            path.concat(j),
            array
          )
        }
      }
      return array
    } else if (schema.items) {
      for (let i = 0; i < array.length; i++) {
        array[i] = super.transform(
          array[i],
          schema.items,
          path.concat(i),
          array
        )
      }
      return array
    } else if (schema.additionalItems) {
      const additionalItems = schema.additionalItems
      for (let i = 0; i < array.length; i++) {
        array[i] = super.transform(
          array[i],
          additionalItems,
          path.concat(i),
          array
        )
      }
      return array
    } else return array
  }

  override validate(
    target: any,
    schema: any,
    path: PropertyKey[],
    parent: any,
    recursion: boolean
  ): void
  override validate(
    target: any,
    schema: RuntimeSchemaSpecific<SchemaArraySpecific>,
    path: PropertyKey[],
    parent: any,
    recursion: boolean
  ): void {
    if (this.validateMetadata(target, schema, isArray, isNotEmpty)) return
    if (schema.minItems && target.length < schema.minItems) {
      this.raise(new SchemaMinItemsError(schema.name, schema.minItems))
    }
    if (schema.maxItems && target.length > schema.maxItems) {
      this.raise(new SchemaMaxItemsError(schema.name, schema.maxItems))
    }
    if (!recursion) return
    if (isArray(schema.items)) {
      for (let i = 0; i < schema.items.length; i++) {
        super.validate(target[i], schema.items[i], path.concat(i), target)
      }
      if (schema.additionalItems) {
        for (let i = schema.items.length; i < target.length; i++) {
          super.validate(
            target[i],
            schema.additionalItems,
            path.concat(i),
            target
          )
        }
      }
    } else if (schema.items) {
      for (let i = 0; i < target.length; i++) {
        super.validate(target[i], schema.items, path.concat(i), target)
      }
    } else if (schema.additionalItems) {
      for (let i = 0; i < target.length; i++) {
        super.validate(
          target[i],
          schema.additionalItems,
          path.concat(i),
          target
        )
      }
    }
  }
}
